/**
 * Role dashboard profiles (Build Contract §1D — should see).
 *
 * Each job role maps to default home dashboard widgets, layout, and home search.
 * Composed at runtime via composeHomeDashboard.ts (featured roles or union merge).
 */

import {
  DEFAULT_HOME_DASHBOARD_CONFIG,
  DEFAULT_HOME_SEARCH,
  type HomeDashboardConfig,
  type HomeSearchConfig,
  type HomeWidgetId,
  type WidgetColumn,
} from '../types/prioritization/homeDashboard';
import { DEFAULT_JOB_ROLES, type JobFamilyId } from '../types/prioritization/jobRoles';
import { SHIPPING_JOB_ROLE_SLUGS } from './contributorNavProfiles';

export interface RoleDashboardProfile {
  /** Ordered widget emphasis for this role */
  widgetIds: HomeWidgetId[];
  widgetColumns?: Partial<Record<HomeWidgetId, WidgetColumn>>;
  widgetHideIfEmpty?: Partial<Record<HomeWidgetId, boolean>>;
  layout?: HomeDashboardConfig['layout'];
  search?: HomeSearchConfig;
}

const FOCUSED_LAYOUT: HomeDashboardConfig['layout'] = 'two-column';
const OPERATIONAL_LAYOUT: HomeDashboardConfig['layout'] = 'three-column';

const FOCUSED_HIDE: Partial<Record<HomeWidgetId, boolean>> = {
  proposals_awaiting_review: true,
  ball_position: true,
  scoring_gaps: true,
  readiness_blockers: true,
  unresolved_dependencies: true,
  saved_searches: true,
};

const OPERATIONAL_HIDE: Partial<Record<HomeWidgetId, boolean>> = {
  ...FOCUSED_HIDE,
  getting_started_checklist: false,
};

function profile(partial: RoleDashboardProfile): RoleDashboardProfile {
  return partial;
}

const FAMILY_BASELINES: Record<JobFamilyId, RoleDashboardProfile> = {
  backlog: profile({
    widgetIds: [
      'my_responsibilities',
      'items_requiring_attention',
      'intake_queue',
      'readiness_blockers',
      'recent_activity',
    ],
    layout: OPERATIONAL_LAYOUT,
    widgetHideIfEmpty: OPERATIONAL_HIDE,
  }),
  measurement: profile({
    widgetIds: [
      'my_responsibilities',
      'scoring_gaps',
      'upcoming_sessions',
      'items_requiring_attention',
    ],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: FOCUSED_HIDE,
  }),
  sessions: profile({
    widgetIds: [
      'my_responsibilities',
      'upcoming_sessions',
      'stage_progress',
      'watchlist',
      'recent_activity',
    ],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: FOCUSED_HIDE,
  }),
  cycles: profile({
    widgetIds: [
      'active_cycles',
      'active_tracks',
      'cycle_readiness',
      'upcoming_sessions',
      'intake_queue',
      'readiness_blockers',
      'stage_progress',
    ],
    layout: OPERATIONAL_LAYOUT,
    widgetHideIfEmpty: OPERATIONAL_HIDE,
  }),
  governance: profile({
    widgetIds: [
      'proposals_awaiting_review',
      'ball_position',
      'my_responsibilities',
      'watchlist',
      'recent_publications',
    ],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: {
      ...FOCUSED_HIDE,
      proposals_awaiting_review: true,
      ball_position: true,
    },
  }),
  platform: profile({
    widgetIds: [
      'saved_searches',
      'recent_publications',
      'recent_activity',
      'objectives_progress',
      'plans_of_record',
      'my_responsibilities',
    ],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: FOCUSED_HIDE,
  }),
};

const ROLE_OVERRIDES: Partial<Record<string, RoleDashboardProfile>> = {
  'idea-submitter': profile({
    widgetIds: ['getting_started', 'my_responsibilities', 'intake_queue', 'watchlist'],
    layout: FOCUSED_LAYOUT,
    search: { entityType: 'items', filters: [], sort: { field: 'updatedAt', direction: 'desc' }, viewMode: 'list' },
  }),
  'decision-authority': profile({
    widgetIds: [
      'proposals_awaiting_review',
      'ball_position',
      'watchlist',
      'my_responsibilities',
      'recent_publications',
    ],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: {
      proposals_awaiting_review: true,
      ball_position: true,
      saved_searches: true,
    },
    search: { entityType: 'items', filters: [], sort: { field: 'updatedAt', direction: 'desc' }, viewMode: 'list' },
  }),
  'ball-holder': profile({
    widgetIds: ['ball_position', 'my_responsibilities', 'watchlist', 'upcoming_sessions'],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: { ball_position: true },
  }),
  'catchball-contributor': profile({
    widgetIds: ['ball_position', 'my_responsibilities', 'watchlist'],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: { ball_position: true },
  }),
  'selection-authority': profile({
    widgetIds: ['proposals_awaiting_review', 'ball_position', 'my_responsibilities', 'watchlist'],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: { proposals_awaiting_review: true, ball_position: true },
  }),
  'priority-setter': profile({
    widgetIds: ['recent_publications', 'my_responsibilities', 'active_tracks', 'watchlist'],
    layout: FOCUSED_LAYOUT,
  }),
  'priority-approver': profile({
    widgetIds: ['proposals_awaiting_review', 'recent_publications', 'my_responsibilities', 'watchlist'],
    layout: FOCUSED_LAYOUT,
    widgetHideIfEmpty: { proposals_awaiting_review: true },
  }),
  'item-triage': profile({
    widgetIds: [
      'intake_queue',
      'items_requiring_attention',
      'readiness_blockers',
      'my_responsibilities',
      'recent_activity',
    ],
    layout: OPERATIONAL_LAYOUT,
    search: { entityType: 'items', filters: [], sort: { field: 'updatedAt', direction: 'desc' }, viewMode: 'table' },
  }),
  'session-participant': profile({
    widgetIds: ['upcoming_sessions', 'my_responsibilities', 'stage_progress', 'watchlist'],
    layout: FOCUSED_LAYOUT,
  }),
  'reports-analyst': profile({
    widgetIds: ['saved_searches', 'recent_publications', 'recent_activity', 'objectives_progress', 'my_responsibilities'],
    layout: FOCUSED_LAYOUT,
  }),
  'plan-of-record-manager': profile({
    widgetIds: ['plans_of_record', 'objectives_progress', 'recent_publications', 'my_responsibilities'],
    layout: OPERATIONAL_LAYOUT,
  }),
};

const WORKSPACE_MEMBER_BASELINE: RoleDashboardProfile = profile({
  widgetIds: ['getting_started', 'upcoming_sessions', 'my_responsibilities', 'watchlist'],
  layout: FOCUSED_LAYOUT,
  widgetHideIfEmpty: FOCUSED_HIDE,
});

export function getRoleDashboardProfile(jobSlug: string): RoleDashboardProfile {
  if (ROLE_OVERRIDES[jobSlug]) return ROLE_OVERRIDES[jobSlug]!;
  const role = DEFAULT_JOB_ROLES.find((r) => r.slug === jobSlug);
  if (role) return FAMILY_BASELINES[role.family];
  return WORKSPACE_MEMBER_BASELINE;
}

export type RoleDashboardProfileSource = 'role-override' | 'family-baseline' | 'workspace-member-baseline';

export function getRoleDashboardProfileSource(jobSlug: string): RoleDashboardProfileSource {
  if (ROLE_OVERRIDES[jobSlug]) return 'role-override';
  if (DEFAULT_JOB_ROLES.find((r) => r.slug === jobSlug)) return 'family-baseline';
  return 'workspace-member-baseline';
}

export function getFamilyBaselineProfile(family: JobFamilyId): RoleDashboardProfile {
  return FAMILY_BASELINES[family];
}

export function getWorkspaceMemberDashboardProfile(): RoleDashboardProfile {
  return WORKSPACE_MEMBER_BASELINE;
}

/** Enterprise default for admin shell users without job-role assignments. */
export function getAdminShellDashboardProfile(): RoleDashboardProfile {
  return {
    widgetIds: DEFAULT_HOME_DASHBOARD_CONFIG.widgetIds,
    widgetColumns: DEFAULT_HOME_DASHBOARD_CONFIG.widgetColumns,
    widgetHideIfEmpty: DEFAULT_HOME_DASHBOARD_CONFIG.widgetHideIfEmpty,
    layout: DEFAULT_HOME_DASHBOARD_CONFIG.layout,
    search: DEFAULT_HOME_DASHBOARD_CONFIG.search ?? DEFAULT_HOME_SEARCH,
  };
}

/** Every shipping job slug must resolve a profile (sync guard). */
export function assertAllJobRolesHaveDashboardProfiles(): void {
  for (const slug of SHIPPING_JOB_ROLE_SLUGS) {
    getRoleDashboardProfile(slug);
  }
}
